import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AddIcon from "@mui/icons-material/Add";
import React, { useState, useRef } from "react";
import SoftInput from "components/SoftInput";
import { IconButton, Checkbox, FormControlLabel } from "@mui/material";
import StatusFilterSelect from "components/StatusFilterSelect";
import SearchIcon from "@mui/icons-material/Search";
import Table from "examples/Tables/Table";
import CitiesRow, { columns } from "store/action/Cities";
import { useGetCityListQuery } from "store/api/cities.api";
import useStore from "store/hook";
import { toast } from "react-toastify";
import { FadeLoader } from "react-spinners";
import { Link, useLocation } from "react-router-dom";
import emptyUniversity from "assets/images/university/emptyUniversity.png";
import SoftPagination from "components/SoftPagination";
import ChangeCityStatus from "layouts/city/components/changeStatus";
import DeleteCity from "layouts/city/components/deleteCity";
import Formatmessage from "services/Formatmessage";
import useLogout from "services/Handlelogout";
import ChangeSubscriptionRequired from "layouts/city/components/changeSubscriptionRequired";
import useListPageQuery from "services/useListPageQuery";
import { LIST_FILTER_KEYS, showDeletedCheckboxSx, showDeletedLabelSx } from "constants/listFilters";

function Cities() {
  const [cityData, setCityData] = useState([]);
  const { page, setPage, resetPage, status, setStatus, showDeleted, setShowDeleted } =
    useListPageQuery(LIST_FILTER_KEYS.CITIES);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCities, setTotalCities] = useState(0);
  const [regex, setRegex] = React.useState("");
  const [newRegex, setNewRegex] = React.useState("");
  const committedRegex = useRef("");
  const { setDialogue } = useStore();
  const logout = useLogout();
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search}`;
  const resetPageRef = useRef(resetPage);
  resetPageRef.current = resetPage;

  React.useEffect(() => {
    if (regex === committedRegex.current) return;

    const timeoutId = setTimeout(() => {
      committedRegex.current = regex;
      resetPageRef.current();
      setNewRegex(regex);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [regex]);

  const { data, isLoading, isFetching } = useGetCityListQuery({
    page: page,
    limit: 20,
    type: newRegex ? "search" : "list",
    search: newRegex,
    status: status === "all" ? undefined : status,
    show_deleted: showDeleted,
  });

  React.useEffect(() => {
    if (data?.status === 1) {
      setCityData(data?.data?.cities || []);
      setTotalPages(data?.data?.totalPages || 0);
      setTotalCities(data?.data?.count ?? data?.data?.totalCities ?? 0);
    } else if (data?.status == 0) {
      toast.error(Formatmessage(data?.message));
    } else if (data?.status == 10) {
      logout();
    }
  }, [data]);

  const rows = React.useMemo(
    () => (cityData ? cityData?.map((city) => CitiesRow(city, setDialogue, returnPath)) : []),
    [cityData, returnPath]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        sx={{
          pr: { xs: 1, sm: 2, md: 4 },
          pl: { xs: 1, sm: 2, md: 2 },
          top: { xs: "55%", sm: "40%" },
        }}
        position="absolute"
        width="100%"
      >
        <SoftBox
          border="1px solid #D8E0E8"
          width="100%"
          borderRadius="md"
          sx={{ backgroundColor: "white.main" }}
        >
          <SoftBox
            sx={{ p: 2 }}
            gap={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
          >
            <SoftBox display="flex" sx={{ alignItems: "center", justifyContent: "start", gap: 2 }}>
              <SoftTypography
                fontWeight="medium"
                sx={{ fontSize: 16, color: "black.main" }}
              >
                Cities
              </SoftTypography>

              <SoftBox borderRadius="xl" sx={{ backgroundColor: "skyblue.main", px: 2, py: 0.1 }}>
                <SoftTypography sx={{ fontSize: 16, fontWeight: "medium", color: "primary.main" }}>
                  {totalCities}
                </SoftTypography>
              </SoftBox>
            </SoftBox>

            <SoftBox
              display="flex"
              flexWrap="wrap"
              sx={{ alignItems: "center", justifyContent: "start", gap: 2 }}
            >
              <SoftBox
                sx={{ position: "relative", height: "45px", width: { xs: "100%", sm: "230px" } }}
              >
                <SoftBox
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "5px",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <IconButton sx={{ transition: "0.3s ease-in-out" }}>
                    <SearchIcon sx={{ color: "black.main", width: 20, height: 20 }} />
                  </IconButton>
                </SoftBox>

                <SoftInput
                  name="search"
                  type="text"
                  value={regex}
                  onChange={(e) => setRegex(e.target.value)}
                  placeholder="Search by city"
                  fontSize="14px"
                  sx={{
                    width: "100% !important",
                    py: "10px",
                    pl: "40px",
                  }}
                />
              </SoftBox>

              <StatusFilterSelect
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                    sx={showDeletedCheckboxSx}
                  />
                }
                label="Show deleted"
                sx={showDeletedLabelSx}
              />

              <SoftButton
                component={Link}
                to="/add-city"
                color="primary"
                variant="contained"
                sx={{
                  fontSize: 14,
                  textTransform: "capitalize",
                  gap: 0.5,
                  width: { xs: "100%", sm: "180px" },
                  fontWeight: 500,
                  maxHeight: "45px",
                }}
              >
                <AddIcon sx={{ height: 20, width: 20 }} /> Add New City
              </SoftButton>
            </SoftBox>
          </SoftBox>

          {isLoading || isFetching ? (
            <SoftBox
              sx={{
                height: "calc(60vh)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FadeLoader color="#3DC2EB" />
            </SoftBox>
          ) : (
            <SoftBox>
              {cityData?.length > 0 ? (
                <SoftBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                    // "& th": {
                    //   fontWeight: "500 !important",
                    // },
                  }}
                >
                  <Table columns={columns} rows={rows} />
                </SoftBox>
              ) : (
                <SoftBox
                  sx={{
                    height: "calc(70vh)",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "start",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    gap={8}
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                  >
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      sx={{ backgroundColor: "skyblue.main" }}
                    >
                      {columns.map(({ name, align, width }, index) => (
                        <SoftBox
                          key={index}
                          width={width}
                          align={align}
                          fontSize="14px"
                          py="10px"
                          px="24px"
                          color="black"
                          sx={{ fontWeight: 500, textTransform: "capitalize" }}
                        >
                          {name}
                        </SoftBox>
                      ))}
                    </SoftBox>

                    <SoftBox display="flex" flexDirection="column" alignItems="center">
                      <SoftBox
                        height="200px"
                        width="200px"
                        borderRadius="lg"
                        sx={{
                          backgroundImage: `url(${emptyUniversity})`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          backgroundSize: "contain",
                        }}
                      />

                      <SoftTypography fontWeight="bold" color="black" sx={{ fontSize: 20 }}>
                        No Cities Available
                      </SoftTypography>
                      <SoftTypography fontWeight="medium" color="gray" sx={{ fontSize: 12 }}>
                        We couldn&apos;t find any cities for your search. Please refine your keywords
                        or add a new city to the system.
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          )}
        </SoftBox>

        <SoftPagination Totalpages={totalPages || 0} currentPage={page} changePage={setPage} />
        <ChangeCityStatus />
        <DeleteCity />
        <ChangeSubscriptionRequired />
      </SoftBox>
    </DashboardLayout>
  );
}

export default Cities;
